import { Icon } from '@iconify/react';
import type { Shipment } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { EmptyState, StatusChip } from '../ui';

/** A read-only snapshot of saved shipment data, independent of the General draft. */
export default function ShipmentBaseTracking({
  shipment,
}: {
  shipment: Shipment | null;
}) {
  const { t, currentLanguage } = useTranslation('shipments');
  if (!shipment) {
    return (
      <EmptyState title={t('No se pudieron cargar los datos logísticos.')} />
    );
  }
  const value = (text?: string | null) => text?.trim() || t('Sin informar');
  const location = (port?: string | null, country?: string | null) =>
    [port?.trim(), country?.trim()].filter(Boolean).join(' · ') ||
    t('Sin informar');
  const date = (text?: string | null) => {
    if (!text) return t('Sin informar');
    const parsed = new Date(`${text.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return t('Sin informar');
    const locale =
      currentLanguage === 'es'
        ? 'es-CL'
        : currentLanguage === 'zh'
          ? 'zh-CN'
          : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsed);
  };
  const transportLabels: Record<string, string> = {
    M: t('Marítimo'),
    A: t('Aéreo'),
    T: t('Terrestre'),
  };
  const transport = shipment.ship_type?.trim() || '';
  const details = [
    { label: t('Contenedor'), value: value(shipment.container_number) },
    { label: t('AWB'), value: value(shipment.awb_number) },
    {
      label: t('Tipo de transporte'),
      value: transportLabels[transport.toUpperCase()] || value(transport),
    },
    { label: t('Nave / vuelo'), value: value(shipment.ship_name) },
  ];
  return (
    <section
      aria-label={t('Datos logísticos del despacho')}
      className='flex flex-col gap-4 pt-4'
    >
      <div className='flex flex-col items-start gap-2'>
        <StatusChip>{t('Sin seguimiento automático')}</StatusChip>
        <p className='text-sm text-default-500'>
          {t(
            'Datos guardados en el despacho. Las fechas informadas están pendientes de confirmación por seguimiento.'
          )}
        </p>
      </div>
      <div className='grid grid-cols-1 items-center gap-4 rounded-medium border border-default-200 p-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]'>
        <dl className='flex min-w-0 flex-col gap-3'>
          <div>
            <dt className='text-xs text-default-500'>{t('Origen')}</dt>
            <dd className='break-words font-semibold'>
              {location(
                shipment.ship_origin_port,
                shipment.ship_origin_country
              )}
            </dd>
          </div>
          <div>
            <dt className='text-xs text-default-500'>{t('ETD informada')}</dt>
            <dd>{date(shipment.ship_etd)}</dd>
          </div>
        </dl>
        <Icon
          icon='tabler:arrow-right'
          width={24}
          className='rotate-90 justify-self-center text-default-400 sm:rotate-0'
          aria-hidden='true'
        />
        <dl className='flex min-w-0 flex-col gap-3 sm:text-right'>
          <div>
            <dt className='text-xs text-default-500'>{t('Destino')}</dt>
            <dd className='break-words font-semibold'>
              {location(
                shipment.ship_destination_port,
                shipment.ship_destination_country
              )}
            </dd>
          </div>
          <div>
            <dt className='text-xs text-default-500'>{t('ETA informada')}</dt>
            <dd>{date(shipment.ship_eta)}</dd>
          </div>
        </dl>
      </div>
      <dl className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {details.map((item) => (
          <div key={item.label}>
            <dt className='text-xs text-default-500'>{item.label}</dt>
            <dd className='break-words font-medium'>{item.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
